
import {Component, Emit, Ref, Vue} from "vue-property-decorator";
import BaseDialog from "@/components/common/BaseDialog.vue";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import {DeviceCategoryTemplate} from "@/data/models/DeviceCategoryTemplate";
import {addNewCategory, editCategoryName,} from "@/data/models/DeviceSettingsManager";
import { DeviceCategoriesAndTemplatesModule } from "@/store/modules/DeviceCategoriesAndTemplatesModule";
import { defineCategoriesAndTemplatesClientAccess } from "@/permissions/ClientPermissions";

@Component({
  components: { BaseDialog },
})
export default class EditCategoryDialog extends Vue {

  @Ref("baseDialog")
  baseDialog: BaseDialogComponent;
  isLoading = false;
  title = "";
  newTitle = "";
  dialogDescription = "";
  isNewCategory = false;
  selectedCategory: DeviceCategoryTemplate | null = null;
  isShowing = false;
  errorExistingCategoryName: string[] = [];

  get isAllowedToDefineOwnDeviceCategoriesAndTemplates() {
    return defineCategoriesAndTemplatesClientAccess();
  }

  get sharedDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.sharedDeviceCategoriesAndTemplates;
  }

  get localDeviceSettingsJson() {
    return DeviceCategoriesAndTemplatesModule.localDeviceCategoriesAndTemplates;
  }

  get deviceSettingsJson() {
    return this.isAllowedToDefineOwnDeviceCategoriesAndTemplates
      ? this.localDeviceSettingsJson
      : this.sharedDeviceSettingsJson;
  }

  @Emit("onError")
  onError(message: string) {
    this.$emit("onError", message);
  }

  @Emit("onSuccess")
  onSuccess(message: string) {
    this.$emit("onSuccess", message);
    this.baseDialog.isShowing = false;
    this.isShowing = false;
  }

  async show(
    title: string,
    isNewCategory: boolean,
  ) {
    this.title = title;
    this.newTitle = title;
    this.isNewCategory = isNewCategory;

    if (!isNewCategory) {
      this.selectedCategory = this.deviceSettingsJson.deviceCategories.find((cat) => cat.categoryName == this.title) ?? null;
    }

    const dialogTitle = isNewCategory ? this.$t("allDevices.addNewCategory").toString() : this.$t(
      "allDevices.editCategory", [this.title]).toString();

    this.dialogDescription = isNewCategory ? this.$t("allDevices.addNewCategoryDesc").toString() : this.$t(
      "allDevices.editCategoryDesc", [title]).toString();

    this.baseDialog.show({
      title: dialogTitle,
      closeOnSave: false,
      closeOnNeutralAction: this.isLoading,
    });

    this.baseDialog.isShowing = true;
    this.resetErrors();
  }

  async onSave() {
    this.startLoading();
    if (this.isNewCategory) {
      await this.saveNewCategory();
    } else {
      await this.editCategory();
    }
  }

  async saveNewCategory() {
    const deviceSettings = this.deviceSettingsJson;
    if (!deviceSettings) {
      return;
    }
    if (!this.validateCategoryName()) {
      this.errorExistingCategoryName.push(this.$t("allDevices.errorExistingCategoryName").toString())
      this.onError("allDevices.categoryNotSaved");
      this.stopLoading();
      return;
    }
    await addNewCategory(this.newTitle, deviceSettings);
    this.stopLoading();
    this.onSuccess("allDevices.categorySaved");

  }

  async editCategory() {
    if (!this.validateCategoryName()) {
      this.errorExistingCategoryName.push(this.$t("allDevices.errorExistingCategoryName").toString())
      this.onError("allDevices.categoryNotSaved");
      this.stopLoading();
      return;
    }

    if (!this.selectedCategory) {
      this.stopLoading();
      return;
    }
    await editCategoryName(this.newTitle, this.selectedCategory.id, this.deviceSettingsJson);
    this.onSuccess("allDevices.categorySaved");
    this.stopLoading();
  }

  startLoading() {
    this.isLoading = true;
  }

  stopLoading() {
    this.isLoading = false;
  }

  validateCategoryName(): boolean {
    if (!this.deviceSettingsJson) {
      return false;
    }

    return !this.deviceSettingsJson.deviceCategories.some(
      (cat) => cat.categoryName.toLowerCase() == this.newTitle.toLowerCase());
  }

  resetErrors() {
    this.errorExistingCategoryName = [];
  }
}
